import React, { useEffect, useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/Layout/Sidebar';
import FormRenderer from './components/Forms/FormRenderer';
import { formConfig } from './config/formConfig';
import SafeIcon from './common/SafeIcon';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center bg-white">
          <img src={formConfig.app.branding.logo} className="w-16 h-16 mb-6" />
          <h1 className="text-2xl font-black mb-2">Something went wrong.</h1>
          <button onClick={() => window.location.reload()} className="mt-6 px-8 py-3 bg-blue-600 text-white font-bold rounded-xl">Refresh</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PublicShareView = ({ routeId }) => {
  const location = useLocation();
  const route = formConfig.site.routes.find(r => r.id === routeId);
  const form = route ? formConfig.forms.find(f => f.id === route.targetId) : null;
  const providedKey = new URLSearchParams(location.search).get(formConfig.access.publicShare.queryParam);

  if (!route || !form) return <div className="p-20 text-center font-bold">Form Not Found</div>;
  if (route.shareKey && providedKey !== route.shareKey) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--background)]">
        <div className="w-20 h-20 mb-8 bg-white p-4 rounded-2xl shadow-xl border border-[var(--border)]">
          <img src={formConfig.app.branding.logo} className="w-full h-full object-contain" />
        </div>
        <div className="p-12 bg-[var(--surface)] border border-[var(--border)] rounded-[40px] shadow-2xl text-center max-w-md">
          <SafeIcon name="Shield" className="text-4xl text-[var(--danger)] mx-auto mb-4" />
          <h2 className="text-2xl font-black mb-2">Access Denied</h2>
          <p className="text-[var(--textMuted)]">The security key for this link is invalid or has expired.</p>
        </div>
      </div>
    );
  }
  return <div className="py-12"><FormRenderer form={form} isPublicMode={true} route={route} /></div>;
};

const HubGate = ({ children }) => {
  const [key, setKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const required = import.meta.env[formConfig.access.hub.accessKeyEnvVar];

  useEffect(() => { if (!required || localStorage.getItem('bpa_key') === required) setAuthed(true); }, [required]);

  if (!authed && required) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)] p-6">
        <div className="w-full max-w-md bg-[var(--surface)] p-12 rounded-[40px] border border-[var(--border)] shadow-2xl text-center">
          <img src={formConfig.app.branding.logo} className="w-16 h-16 mx-auto mb-8" />
          <h1 className="text-3xl font-black mb-8 italic">BPA Hub</h1>
          <form onSubmit={(e) => { e.preventDefault(); if (key === required) { localStorage.setItem('bpa_key', key); setAuthed(true); } else alert('Invalid Key'); }} className="space-y-4">
            <input type="password" placeholder="Hub Access Key" className="w-full p-4 rounded-2xl bg-[var(--background)] border border-[var(--border)] text-center outline-none" value={key} onChange={(e) => setKey(e.target.value)} />
            <button className="w-full py-4 bg-[var(--primary)] text-white font-black rounded-2xl">Enter Hub</button>
          </form>
        </div>
      </div>
    );
  }
  return children;
};

const AppContent = () => {
  const location = useLocation();
  const isPublic = location.pathname.startsWith('/f/');

  useEffect(() => {
    const route = formConfig.site.routes.find(r => r.path === location.pathname || r.sharePath === location.pathname);
    document.title = route ? `BPA Solutions - ${route.label}` : "BPA Solutions - Forms";
  }, [location]);

  return (
    <div className="min-h-screen bg-[var(--background)] flex">
      {!isPublic && <Sidebar />}
      <main className={`flex-1 ${isPublic ? 'w-full' : 'pl-[280px]'}`}>
        <header className="h-20 border-b border-[var(--border)] flex items-center justify-between px-12 sticky top-0 bg-[var(--background)]/80 backdrop-blur-xl z-50">
          <div className="flex items-center gap-4">
            <img src={formConfig.app.branding.logo} className="h-8" />
            <span className="text-[10px] font-black uppercase tracking-widest opacity-50">BPA Solutions</span>
          </div>
          {!isPublic && <div className="w-10 h-10 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center font-black text-xs">AD</div>}
        </header>
        <div className="p-12">
          <Routes>
            {formConfig.site.routes.map(r => (
              <React.Fragment key={r.id}>
                <Route path={r.path} element={<HubGate>{r.type === 'form' ? <FormRenderer form={formConfig.forms.find(f => f.id === r.targetId)} route={r} /> : <div>Page Content</div>}</HubGate>} />
                {r.sharePath && <Route path={r.sharePath} element={<PublicShareView routeId={r.id} />} />}
              </React.Fragment>
            ))}
            <Route path="/" element={<Navigate to="/website-intake" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default function App() { return <ErrorBoundary><ThemeProvider><Router><AppContent /></Router></ThemeProvider></ErrorBoundary>; }